import { useMutation, useQuery } from "@tanstack/react-query";
import { httpClient } from "utils/Api";
import { API_URL, QueryKeysEnums } from "utils/Constant";
import { formQueryParametersFromCriteria } from "utils/Helpers";
import { queryClient } from "utils/queryClient";

// Helper to get endpoint from slug
const getEspnEndpoint = (slug) => `${slug.replace("-records", "")}/`;
const staleTime = 1000 * 60 * 5; // 5 minutes

// List records for a given ESPN slug
export function useEspnRecords({
  slug,
  filters = {},
  page = 1,
  page_size = 10,
  options = {},
}) {
  return useQuery({
    queryKey: [QueryKeysEnums.ESPN_RECORDS, slug, filters, page, page_size],
    queryFn: async () => {
      const qs = formQueryParametersFromCriteria({
        ...filters,
        page,
        page_size,
      });
      
      const endpoint = getEspnEndpoint(slug);
      const res = await httpClient.get(`${endpoint}${qs}`);
      return res?.data ?? [];
    },
    enabled: !!slug,
    retry: 1,
    ...options,
  });
}

// Create a record under a slug
export function useCreateEspnRecord({ slug, options = {} }) {
  return useMutation({
    mutationFn: (data) => {
      const endpoint = getEspnEndpoint(slug);
      return httpClient.post(endpoint, data);
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries([QueryKeysEnums.ESPN_RECORDS, slug]);
      if (options.onSuccess) options.onSuccess(...args);
    },
    onError: options.onError,
  });
}

// Update a record under a slug
export function useUpdateEspnRecord({ slug, options = {} }) {
  return useMutation({
    mutationFn: ({ id, data, endpoint }) => {
      const baseEndpoint = getEspnEndpoint(slug);
      const url = endpoint 
        ? `${baseEndpoint}${id}/${endpoint}/`
        : `${baseEndpoint}${id}/`;
      return httpClient.patch(url, data);
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries([QueryKeysEnums.ESPN_RECORDS, slug]);
      if (options.onSuccess) options.onSuccess(...args);
    },
    onError: options.onError,
  });
}


export function useStates(options = {}) {
  return useQuery({
    queryKey: [QueryKeysEnums.USERS, "states"],
    queryFn: async () => {
      const res = await httpClient.get("/state/");
      return res?.data ?? [];
    },
    staleTime,
    ...options,
  });
}

export function useLocalGovernmentAreas(stateId, options = {}) {
  const deriveStateId = (s) => {
    if (s == null || s === "") return null;
    if (typeof s === "object") return s.id ?? s.value ?? s.pk ?? null;
    return s;
  };

  const sid = deriveStateId(stateId);

  return useQuery({
    queryKey: [QueryKeysEnums.USERS, "lga", sid],
    queryFn: async () => {
      if (!sid) return [];
      const res = await httpClient.get(`/local_government_area/`, {
        params: { state: sid },
      });
      return res?.data ?? [];
    },
    enabled: !!sid,
    staleTime,
    ...options,
  });
}

export function useGetSampleTypes(options = {}) {
  return useQuery({
    queryKey: [QueryKeysEnums.ESPN_RECORDS, API_URL.ESPN.SAMPLE_TYPE],
    queryFn: async () => {
      const res = await httpClient.get(API_URL.ESPN.SAMPLE_TYPE);
      return res?.data ?? [];
    },
    staleTime,
    ...options,
  });
}

export function useDashboardStats({ dateRange, state, options = {} } = {}) {
  return useQuery({
    queryKey: [API_URL.ESPN.DASHBOARD_STATS, dateRange, state],
    queryFn: async () => {
      const params = { date_range: dateRange };
      if (state) params.state = state;
      const res = await httpClient.get(API_URL.ESPN.DASHBOARD_STATS, {
        params,
      });
      return res?.data ?? {};
    },
    enabled: !!dateRange,
    retry: 1,
    staleTime,
    ...options,
  });
}

export function useDashboardSummary(params = {}) {
  return useQuery({
    queryKey: [API_URL.ESPN.DASHBOARD_SUMMARY, params],
    queryFn: async () => {
      const res = await httpClient.get(API_URL.ESPN.DASHBOARD_SUMMARY, {
        params,
      });
      return res?.data ?? {};
    },
    staleTime,
  });
}

export function useExportDashboard(options = {}) {
  return useMutation({
    mutationFn: async (payload) => {
      const res = await httpClient.post(
        API_URL.ESPN.DASHBOARD_EXPORT,
        payload,
        {
          responseType: "blob",
        },
      );
      return res?.data ?? null;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export const useEspnDashboardCounts = (options = {}) => {
  return useQuery({
    queryKey: ["espn-dashboard-counts"],
    queryFn: () => httpClient.get("/espn/dashboard-counts/").then((res) => res.data),
    staleTime: 30000,
    ...options,
  });
};